import { useEffect } from "react";
import { NavLink } from "react-router-dom";
import { Home, SlidersHorizontal, MapPin, Plus } from "lucide-react";
import type { ComponentType } from "react";
import type { LucideProps } from "lucide-react";
import { useMobileOverlayControls } from "@/contexts/MobileOverlayContext";
import MobileFiltersDrawer from "@/components/MobileFiltersDrawer";
import MobileMapStrip from "@/components/MobileMapStrip";

interface NavItem {
  label: string;
  icon: ComponentType<LucideProps>;
  to: string;
  cta: boolean;
  action?: "filters" | "map";
}

const items: NavItem[] = [
  { label: "Início", icon: Home, to: "/", cta: false },
  { label: "Filtros", icon: SlidersHorizontal, to: "/", cta: false, action: "filters" },
  { label: "Mapa", icon: MapPin, to: "/?view=map", cta: false, action: "map" },
  { label: "Anunciar", icon: Plus, to: "/publicar", cta: true },
];

export default function MobileBottomBar(): React.JSX.Element {
  const { filtersOpen, mapOpen, openFilters, closeFilters, openMap, closeMap } =
    useMobileOverlayControls();

  useEffect(() => {
    const open = filtersOpen || mapOpen;
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [filtersOpen, mapOpen]);

  return (
    <>
      <MobileFiltersDrawer open={filtersOpen} onClose={closeFilters} />
      <MobileMapStrip open={mapOpen} onClose={closeMap} />
      <nav
        data-testid="mobile-bottom-bar"
        aria-label="Navegação inferior"
        className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white/95 backdrop-blur-md border-t border-cream-200 pb-[env(safe-area-inset-bottom)]"
      >
        <div className="flex min-h-[56px]">
          {items.map(({ label, icon: Icon, to, cta, action }) => {
            if (action === "filters") {
              return (
                <button
                  key={label}
                  type="button"
                  onClick={openFilters}
                  className={[
                    "flex-1 flex flex-col items-center justify-center py-2.5 min-h-[44px]",
                    filtersOpen ? "text-heat-600" : "text-ink-500",
                  ].join(" ")}
                  aria-label="Abrir filtros"
                  aria-expanded={filtersOpen}
                >
                  <Icon className="w-5 h-5 mb-0.5" strokeWidth={2} />
                  <span className={`text-[10px] font-bold uppercase tracking-wider mt-0.5 ${filtersOpen ? "opacity-100" : "opacity-70"}`}>
                    {label}
                  </span>
                  {filtersOpen && <span className="w-1 h-1 rounded-full bg-heat-600 mt-0.5" />}
                </button>
              );
            }
            if (action === "map") {
              return (
                <button
                  key={label}
                  type="button"
                  onClick={openMap}
                  className={[
                    "flex-1 flex flex-col items-center justify-center py-2.5 min-h-[44px]",
                    mapOpen ? "text-heat-600" : "text-ink-500",
                  ].join(" ")}
                  aria-label="Abrir mapa"
                  aria-expanded={mapOpen}
                >
                  <Icon className="w-5 h-5 mb-0.5" strokeWidth={2} />
                  <span className={`text-[10px] font-bold uppercase tracking-wider mt-0.5 ${mapOpen ? "opacity-100" : "opacity-70"}`}>
                    {label}
                  </span>
                  {mapOpen && <span className="w-1 h-1 rounded-full bg-heat-600 mt-0.5" />}
                </button>
              );
            }
            return (
              <NavLink
                key={label}
                to={to}
                className={({ isActive }) =>
                  [
                    "flex-1 flex flex-col items-center justify-center py-2.5 min-h-[44px]",
                    cta
                      ? "bg-heat-grad text-white"
                      : isActive
                        ? "text-heat-600"
                        : "text-ink-500",
                  ].join(" ")
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon className="w-5 h-5 mb-0.5" strokeWidth={2} />
                    <span className={`text-[10px] font-bold uppercase tracking-wider mt-0.5 ${cta ? "" : isActive ? "opacity-100" : "opacity-70"}`}>
                      {label}
                    </span>
                    {isActive && !cta && <span className="w-1 h-1 rounded-full bg-heat-600 mt-0.5" />}
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </>
  );
}
